import React from 'react';
import AssistantBotRow from '../shells/AssistantBotRow';
import UserReplyRow from '../shells/UserReplyRow';
import {
  dormRulesBubbleText,
  mealIntroBubbleText,
  mealRulesBubbleText,
  stepQuestionKey,
  travelReceiptBubbleText,
} from './siteArrivalConfirmationSteps';
import type { useSiteArrivalConfirmationFlow } from './useSiteArrivalConfirmationFlow';
import type { SiteArrivalConfirmationStep } from './siteArrivalConfirmationTypes';

type Flow = ReturnType<typeof useSiteArrivalConfirmationFlow>;

function questionForStep(stepId: SiteArrivalConfirmationStep, t: Flow['t']): string {
  if (stepId === 'travelReceiptAcknowledged') return travelReceiptBubbleText(t);
  if (stepId === 'agreeDorm') return dormRulesBubbleText(t);
  if (stepId === 'agreeMeal') return mealRulesBubbleText(t);
  if (stepId === 'breakfast') return mealIntroBubbleText(t) + '\n\n' + t(stepQuestionKey(stepId));
  return t(stepQuestionKey(stepId));
}

function isRulesStep(stepId: SiteArrivalConfirmationStep): boolean {
  return stepId === 'agreeDorm' || stepId === 'agreeMeal';
}

function isRentStep(stepId: SiteArrivalConfirmationStep): boolean {
  return stepId === 'rentTv' || stepId === 'rentFutons' || stepId === 'rentFridge' || stepId === 'rentOther';
}

export default function SiteArrivalConfirmationChatThread({ flow }: { flow: Flow }) {
  const { t, committed, isComplete, currentStep, assistantQuestionVisible } = flow;
  const undoLabel = t('submitDocuments.chat.undo');
  const editLabel = t('submitDocuments.fillFormsChecklist.edit');

  const committedStepIds = new Set(committed.map((c) => c.stepId));

  return (
    <>
      {committed.map((c, i) => (
        <React.Fragment key={`${c.stepId}-${i}`}>
          {isRulesStep(c.stepId) && (
            <AssistantBotRow animateIn>{questionForStep(c.stepId, t)}</AssistantBotRow>
          )}
          {c.stepId === 'breakfast' && (
            <AssistantBotRow animateIn>{mealIntroBubbleText(t)}</AssistantBotRow>
          )}
          {c.stepId === 'rentTv' && (
            <AssistantBotRow animateIn>{t('submitDocuments.siteArrivalConfirmation.dormRentIntro')}</AssistantBotRow>
          )}
          {!isRulesStep(c.stepId) && c.stepId !== 'breakfast' && !isRentStep(c.stepId) && (
            <AssistantBotRow animateIn>{questionForStep(c.stepId, t)}</AssistantBotRow>
          )}
          {c.stepId === 'breakfast' && (
            <AssistantBotRow animateIn>{t(stepQuestionKey('breakfast'))}</AssistantBotRow>
          )}
          {isRentStep(c.stepId) && (
            <AssistantBotRow animateIn>{t(stepQuestionKey(c.stepId))}</AssistantBotRow>
          )}
          <UserReplyRow
            displayText={c.displayText}
            showEdit={flow.showEditForCommitIndex(i)}
            onEdit={() => flow.jumpToEditCommitIndex(i)}
            editLabel={editLabel}
            showUndo={flow.showUndoForCommitIndex(i)}
            onUndo={flow.undo}
            undoLabel={undoLabel}
          />
        </React.Fragment>
      ))}

      {!isComplete && currentStep && assistantQuestionVisible && (
        <>
          {isRulesStep(currentStep.stepId) && !committedStepIds.has(currentStep.stepId) && (
            <AssistantBotRow animateIn>{questionForStep(currentStep.stepId, t)}</AssistantBotRow>
          )}
          {currentStep.stepId === 'breakfast' && !committedStepIds.has('breakfast') && (
            <>
              <AssistantBotRow animateIn>{mealIntroBubbleText(t)}</AssistantBotRow>
              <AssistantBotRow animateIn>{t(stepQuestionKey('breakfast'))}</AssistantBotRow>
            </>
          )}
          {currentStep.stepId === 'rentTv' && !committedStepIds.has('rentTv') && (
            <>
              <AssistantBotRow animateIn>{t('submitDocuments.siteArrivalConfirmation.dormRentIntro')}</AssistantBotRow>
              <AssistantBotRow animateIn>{t(stepQuestionKey('rentTv'))}</AssistantBotRow>
            </>
          )}
          {!isRulesStep(currentStep.stepId) &&
            currentStep.stepId !== 'breakfast' &&
            currentStep.stepId !== 'rentTv' &&
            !isRentStep(currentStep.stepId) && (
            <AssistantBotRow animateIn>{questionForStep(currentStep.stepId, t)}</AssistantBotRow>
          )}
          {isRentStep(currentStep.stepId) &&
            currentStep.stepId !== 'rentTv' &&
            !committedStepIds.has(currentStep.stepId) && (
            <AssistantBotRow animateIn>{t(stepQuestionKey(currentStep.stepId))}</AssistantBotRow>
          )}
        </>
      )}
    </>
  );
}
